// shared/src/util/tz-clock.ts
// Generic IANA-timezone wall-clock helper — generalizes etPartsAt (et-clock.ts)
// to ANY time zone so the parameter-driven cron schedule can express fire times
// as { at: 'HH:MM', tz: 'America/New_York' | 'UTC' | <IANA> } and have DST handled
// automatically by Intl, with NO hand-written UTC cron math or DST pairs.
//
// PERF (per feedback_intl_dateformat_cpu_trap): `new Intl.DateTimeFormat(...)`
// loads ICU locale + tz data on every construction. The cron-dispatcher calls
// wallClockInTz once per time-of-day rule per minute, so the formatters are
// cached per-tz in a module-level Map (built once per isolate, reused via
// formatToParts). The tz set is tiny (ET + UTC in the seed), so the cache is
// bounded.

const FMT_CACHE = new Map<string, Intl.DateTimeFormat>();

function formatterFor(tz: string): Intl.DateTimeFormat {
  let f = FMT_CACHE.get(tz);
  if (f === undefined) {
    f = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year:     'numeric',
      month:    '2-digit',
      day:      '2-digit',
      hour:     '2-digit',
      minute:   '2-digit',
      hour12:   false,
    });
    FMT_CACHE.set(tz, f);
  }
  return f;
}

export interface TzWallClock {
  /** 'YYYY-MM-DD' calendar date in the target tz. */
  date:    string;
  hour:    number;   // 0..23 wall-clock hour in tz
  minute:  number;   // 0..59
  /** minutes since tz-local midnight (hour*60 + minute). */
  minutes: number;
  /** 0=Sun .. 6=Sat — weekday of the tz calendar date. */
  weekday: number;
}

/** Wall-clock breakdown of an epoch-ms instant in an arbitrary IANA tz (DST-aware).
 *  Throws if `tz` is not a valid IANA zone (Intl.DateTimeFormat construction throws). */
export function wallClockInTz(ms: number, tz: string): TzWallClock {
  const parts = formatterFor(tz).formatToParts(new Date(ms));
  const g = (t: string): string => parts.find((p) => p.type === t)!.value;
  const y = g('year'), mo = g('month'), da = g('day');
  let hh = parseInt(g('hour'), 10);
  const mm = parseInt(g('minute'), 10);
  if (hh === 24) hh = 0; // some ICU builds emit '24' for midnight under hour12:false
  const date = `${y}-${mo}-${da}`;
  const weekday = new Date(`${date}T00:00:00Z`).getUTCDay();
  return { date, hour: hh, minute: mm, minutes: hh * 60 + mm, weekday };
}

/** Inverse of wallClockInTz: the epoch-ms instant whose wall clock in `tz` is
 *  exactly (y, mo, da, h, m). DST-aware via offset correction (2 passes nail the
 *  ±1h shift on a transition day). Returns null when that wall time does NOT
 *  exist (spring-forward gap, e.g. 02:30 on the US spring-forward day) — callers
 *  treat a null as "no fire that day", matching a forward minute-scan which would
 *  never land on a non-existent minute.
 *
 *  Note: during a fall-back overlap a wall time occurs twice; this returns one
 *  valid instant (the offset at the first-pass guess). None of the production
 *  schedule's fire minutes fall in the 01:00–02:00 overlap window, so the
 *  ambiguity is never exercised in practice.
 *
 *  Powers the O(days) date-iteration in cron-schedule.ts's next/last-fire
 *  projections — replacing an O(window-minutes) per-minute Intl scan that issued
 *  ~77k formatToParts per schedule-endpoint request and tripped the Free-plan
 *  worker CPU limit (exceededCpu 503). Mirrors feedback_intl_dateformat_cpu_trap. */
export function utcMsForWallClock(
  y: number, mo: number, da: number, h: number, m: number, tz: string,
): number | null {
  const wantDate = `${pad4(y)}-${pad2(mo)}-${pad2(da)}`;
  const wantMin = h * 60 + m;
  let guess = Date.UTC(y, mo - 1, da, h, m);
  for (let pass = 0; pass < 2; pass++) {
    const wc = wallClockInTz(guess, tz);
    const [gy, gmo, gda] = wc.date.split('-').map((s) => parseInt(s, 10)) as [number, number, number];
    const localAsUtc = Date.UTC(gy, gmo - 1, gda, wc.hour, wc.minute);
    const offset = localAsUtc - guess;            // ms the local clock leads UTC
    guess = Date.UTC(y, mo - 1, da, h, m) - offset;
  }
  // Verify the round-trip: rejects the spring-forward gap (the target wall time
  // doesn't exist that day → the corrected guess lands on a different minute).
  const chk = wallClockInTz(guess, tz);
  if (chk.date === wantDate && chk.minutes === wantMin) return guess;
  return null;
}

const PAD = '0000';
function pad2(n: number): string { return (PAD + n).slice(-2); }
function pad4(n: number): string { return (PAD + n).slice(-4); }

/** True if `tz` is a valid IANA time zone (used by the schedule param's Zod
 *  refinement so an invalid zone is rejected at write time, never at fire time). */
export function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}
